import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Calendar, Clock, Info, Loader2, NotebookPen, AlertCircle } from "lucide-react";
import SmartDateInput from "./SmartDateInput";

/**
 * MOT Booking Intent Capture — Stage 3 of the MOT Booking Intelligence Engine.
 *
 * Surfaces a calm, structured place for the FOH to capture the operational
 * shape of an MOT booking:
 *
 *   • Preferred date              (HTML5 date)
 *   • How the visit works         (3 wait-mode chips)
 *   • Latest needed back by       (HTML5 datetime-local, drop-off only)
 *   • Short free-text notes
 *
 * Architectural rules (mirrored from the backend module):
 *   - Captures INTENT, not commitment. No diary write-through. No capacity
 *     check. No bay allocation.
 *   - Each field autosaves on change (with a small debounce). Partial-update
 *     friendly — only the changed key is sent.
 *   - The conversational lead, wait-mode options and FOH hints all come
 *     from the backend catalogue so wording stays single-sourced.
 *
 * Props:
 *   sessionId   — intake session id (required)
 *   isLocked    — when true, all inputs render disabled
 *   className   — optional extra class names for the host card
 */
export default function MOTBookingIntentCard({ sessionId, isLocked = false, className = "" }) {
  const [catalogue, setCatalogue] = useState(null);
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hintMode, setHintMode] = useState(null); // hover preview

  const saveTimers = useRef({});

  // ----- initial load ----------------------------------------------------
  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/intake/sessions/${sessionId}/booking-intent`);
        if (cancelled) return;
        setCatalogue({ lead: r.data.lead, wait_modes: r.data.wait_modes });
        setIntent(r.data.booking_intent);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || "Could not load booking intent");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // ----- save (debounced, partial-update) --------------------------------
  const patchField = (key, value, debounceMs = 350) => {
    if (isLocked) return;
    // Optimistic local state update.
    setIntent((prev) => ({
      ...(prev || {}),
      [key]: value,
      ...(key === "wait_mode"
        ? { wait_mode_label: labelForWaitMode(value, catalogue) }
        : {}),
    }));
    clearTimeout(saveTimers.current[key]);
    saveTimers.current[key] = setTimeout(async () => {
      setSaving(true);
      setError(null);
      try {
        const body = { [key]: value };
        const r = await axios.post(
          `${API}/intake/sessions/${sessionId}/booking-intent`,
          body,
        );
        // Reconcile with server-validated state.
        setIntent(r.data.booking_intent);
      } catch (err) {
        setError(err?.response?.data?.detail || "Could not save booking intent");
      } finally {
        setSaving(false);
      }
    }, debounceMs);
  };

  // ----- derived ---------------------------------------------------------
  const waitModes = catalogue?.wait_modes || [];
  const activeMode = intent?.wait_mode || null;
  const previewedMode = hintMode || activeMode;
  const previewedHint = useMemo(
    () => waitModes.find((m) => m.value === previewedMode)?.hint || null,
    [waitModes, previewedMode],
  );
  // The "needs back by" field is only meaningful for drop-off and
  // collection-later modes — but we render it for both so the workshop
  // knows when the customer wants the vehicle back, even on multi-day
  // collections.
  const showsNeedsBackBy = activeMode === "drop_off" || activeMode === "collection_later";

  // ----- render ----------------------------------------------------------
  if (!sessionId) return null;

  if (loading) {
    return (
      <section
        className={`rounded-lg border border-slate-200 bg-white px-5 py-4 ${className}`}
        data-testid="mot-booking-intent-loading"
      >
        <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 font-semibold flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading booking intent…
        </p>
      </section>
    );
  }

  return (
    <section
      className={`rounded-lg border border-slate-200 bg-white ${className}`}
      data-testid="mot-booking-intent-card"
    >
      <header className="px-5 pt-3.5 pb-2 border-b border-slate-100">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-teal-600" />
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              Booking date & visit shape
            </p>
          </div>
          {saving && (
            <span
              className="text-[11px] text-slate-400 flex items-center gap-1"
              data-testid="mot-booking-intent-saving"
            >
              <Loader2 className="w-3 h-3 animate-spin" /> Saving
            </span>
          )}
        </div>

        {catalogue?.lead?.say_to_customer && (
          <p
            className="mt-2 text-[13.5px] text-teal-900/85 italic leading-snug"
            data-testid="mot-booking-intent-say"
          >
            &ldquo;{catalogue.lead.say_to_customer}&rdquo;
          </p>
        )}
      </header>

      <div className="px-5 py-4 space-y-4">
        {/* Preferred date ------------------------------------------------ */}
        <div>
          <label
            htmlFor={`mot-booking-intent-date-${sessionId}`}
            className="block text-[11.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5"
          >
            Preferred date
          </label>
          <SmartDateInput
            value={intent?.preferred_date || ""}
            onChange={(e) => patchField("preferred_date", e.target.value, 250)}
            disabled={isLocked}
            dataTestid="mot-booking-intent-date"
            hint
          />
        </div>

        {/* MOT slot time + duration live INSIDE the workshop-handover card
            (right beside the Send-to-workshop CTA) so the FOH can't miss
            them. Keeping a second copy here was confusing — they showed up
            twice on the same screen. */}

        {/* Wait mode chips ---------------------------------------------- */}
        <div>
          <p className="text-[11.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5">
            How would they like the visit to work?
          </p>
          <div
            className="grid grid-cols-1 gap-2"
            role="radiogroup"
            data-testid="mot-booking-intent-wait-modes"
          >
            {waitModes.map((opt) => {
              const active = activeMode === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={isLocked}
                  onClick={() => patchField("wait_mode", opt.value, 0)}
                  onMouseEnter={() => setHintMode(opt.value)}
                  onMouseLeave={() => setHintMode(null)}
                  onFocus={() => setHintMode(opt.value)}
                  onBlur={() => setHintMode(null)}
                  className={[
                    "text-left rounded-md border px-3.5 py-2.5 transition-colors",
                    "disabled:opacity-60 disabled:cursor-not-allowed",
                    active
                      ? "border-teal-400 bg-teal-50/70 ring-1 ring-teal-200"
                      : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50",
                  ].join(" ")}
                  data-testid={`mot-booking-intent-wait-${opt.value}`}
                >
                  <span className={`text-[13.5px] font-medium ${active ? "text-teal-900" : "text-slate-800"}`}>
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>

          {previewedHint && (
            <p
              className="mt-2 text-[12.5px] text-slate-500 italic leading-snug flex items-start gap-1.5"
              data-testid="mot-booking-intent-hint"
            >
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-slate-400" />
              <span>{previewedHint}</span>
            </p>
          )}
        </div>

        {/* Needs vehicle back by — conditional --------------------------- */}
        {showsNeedsBackBy && (
          <div data-testid="mot-booking-intent-needs-back-by-block">
            <label
              htmlFor={`mot-booking-intent-back-by-${sessionId}`}
              className="block text-[11.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5 flex items-center gap-1.5"
            >
              <Clock className="w-3 h-3 text-slate-400" />
              Latest they need it back by
            </label>
            <input
              id={`mot-booking-intent-back-by-${sessionId}`}
              type="datetime-local"
              disabled={isLocked}
              value={intent?.needs_vehicle_back_by || ""}
              onChange={(e) => patchField("needs_vehicle_back_by", e.target.value, 350)}
              className="w-full text-[14px] text-slate-800 bg-white border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400 disabled:bg-slate-50 disabled:text-slate-500"
              data-testid="mot-booking-intent-needs-back-by"
            />
            <p className="mt-1 text-[11.5px] text-slate-400 leading-snug">
              Helps the workshop respect their day — not a hard deadline on the diary yet.
            </p>
          </div>
        )}

        {/* Booking notes ------------------------------------------------ */}
        <div>
          <label
            htmlFor={`mot-booking-intent-notes-${sessionId}`}
            className="block text-[11.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5 flex items-center gap-1.5"
          >
            <NotebookPen className="w-3 h-3 text-slate-400" />
            Booking notes (optional)
          </label>
          <textarea
            id={`mot-booking-intent-notes-${sessionId}`}
            rows={2}
            maxLength={400}
            disabled={isLocked}
            placeholder="e.g. needs to drop off before 8am, prefers a courtesy car…"
            value={intent?.notes || ""}
            onChange={(e) => patchField("notes", e.target.value, 600)}
            className="w-full text-[14px] text-slate-800 bg-white border border-slate-200 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400 disabled:bg-slate-50 disabled:text-slate-500"
            data-testid="mot-booking-intent-notes"
          />
        </div>

        {/* Guardrail footer --------------------------------------------- */}
        {catalogue?.lead?.guardrails && (
          <p
            className="text-[11.5px] text-slate-400 italic leading-snug pt-1"
            data-testid="mot-booking-intent-guardrails"
          >
            {catalogue.lead.guardrails}
          </p>
        )}

        {error && (
          <p
            className="text-[12.5px] text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2 flex items-start gap-1.5"
            data-testid="mot-booking-intent-error"
          >
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

function labelForWaitMode(value, catalogue) {
  if (!value || !catalogue?.wait_modes) return null;
  return catalogue.wait_modes.find((m) => m.value === value)?.label || null;
}
